import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Download, Info, Upload } from 'lucide-react';
import { DashboardPageFormBody } from '@shigo-ui/dashboard-ui';
import {
  Alert,
  AlertDescription,
  Button,
  FilePicker,
  RadioGroup,
  RadioGroupItem,
  toast,
} from '@shigo-ui/ui';

import { showRequestError } from '../../../../../../lib/requestErrorToast';
import { downloadBlob } from '../../../../../../lib/downloadBlob';
import { dashboardT } from '../../../../../../lib/messages';
import type { DemandPartnerFormValue } from '../../../formSchema';
import { useDemandPartnerMutations } from '../../../hooks';
import { DemandPartnerTargetingList, DemandPartnerTargetingMode } from '../../../model';

const targetingModeIncluded: Record<string, boolean> = {
  [DemandPartnerTargetingMode.Include]: true,
  [DemandPartnerTargetingMode.Exclude]: false,
};

export interface DemandPartnerBundleDomainListsProps {
  partnerId: string;
  bundleListCount: number;
  domainListCount: number;
}

export function DemandPartnerBundleDomainLists(props: DemandPartnerBundleDomainListsProps) {
  return (
    <DashboardPageFormBody width="lg">
      <section className="border-input overflow-hidden rounded-md border">
        <header className="bg-muted/40 border-input border-b px-4 py-3">
          <h2 className="text-sm font-semibold">
            {dashboardT('demandPartners.targetingLists.title')}
          </h2>
          <p className="text-muted-foreground mt-0.5 text-xs">
            {dashboardT('demandPartners.targetingLists.description')}
          </p>
        </header>
        <div className="divide-border divide-y">
          <DemandPartnerTargetingListRow
            partnerId={props.partnerId}
            list={DemandPartnerTargetingList.Bundle}
            initialCount={props.bundleListCount}
          />
          <DemandPartnerTargetingListRow
            partnerId={props.partnerId}
            list={DemandPartnerTargetingList.Domain}
            initialCount={props.domainListCount}
          />
        </div>
      </section>
      <Alert className="border-cyan-200 bg-cyan-50 text-cyan-950">
        <Info className="h-4 w-4" aria-hidden="true" />
        <AlertDescription>
          {dashboardT('demandPartners.targetingLists.inheritance')}
        </AlertDescription>
      </Alert>
    </DashboardPageFormBody>
  );
}

function DemandPartnerTargetingListRow(props: {
  partnerId: string;
  list: DemandPartnerTargetingList;
  initialCount: number;
}) {
  const form = useFormContext<DemandPartnerFormValue>();
  const mutations = useDemandPartnerMutations();
  const [file, setFile] = useState<File | null>(null);
  const [filePickerKey, setFilePickerKey] = useState(0);
  const [uploadedCount, setUploadedCount] = useState<number | null>(null);
  const count = uploadedCount ?? props.initialCount;
  const bundle = props.list === DemandPartnerTargetingList.Bundle;
  const fieldName = bundle ? 'includeBundles' : 'includeDomains';
  const busy =
    mutations.downloadTargetingListTemplate.isPending ||
    mutations.downloadTargetingList.isPending ||
    mutations.uploadTargetingList.isPending;

  async function downloadTemplate() {
    try {
      const blob = await mutations.downloadTargetingListTemplate.mutateAsync(props.list);
      downloadBlob(blob, bundle ? 'bundlesTemplate.csv' : 'domainsTemplate.csv');
    } catch (error) {
      showRequestError(error, dashboardT('demandPartners.targetingLists.templateError'));
    }
  }

  async function downloadCurrent() {
    if (!props.partnerId) return;
    try {
      const blob = await mutations.downloadTargetingList.mutateAsync({
        id: props.partnerId,
        list: props.list,
      });
      downloadBlob(blob, bundle ? 'exportBundles.csv' : 'exportDomains.csv');
    } catch (error) {
      showRequestError(error, dashboardT('demandPartners.targetingLists.exportError'));
    }
  }

  async function upload() {
    if (!file || !props.partnerId) return;
    try {
      const nextCount = await mutations.uploadTargetingList.mutateAsync({
        id: props.partnerId,
        list: props.list,
        file,
      });
      setUploadedCount(nextCount);
      setFile(null);
      setFilePickerKey((key) => key + 1);
      toast.success(dashboardT('demandPartners.targetingLists.uploaded'));
    } catch (error) {
      showRequestError(error, dashboardT('demandPartners.targetingLists.uploadError'));
    }
  }

  return (
    <div className="grid gap-4 px-4 py-4 xl:grid-cols-[190px_minmax(280px,1fr)_auto] xl:items-end">
      <div className="self-center">
        <h3 className="text-sm font-semibold">
          {dashboardT(
            bundle
              ? 'demandPartners.targetingLists.bundles'
              : 'demandPartners.targetingLists.domains',
          )}
        </h3>
        <p className="text-muted-foreground mt-0.5 text-xs">
          {dashboardT('demandPartners.targetingLists.count', { count })}
        </p>
        <Controller
          control={form.control}
          name={fieldName}
          render={({ field }) => (
            <RadioGroup
              value={
                field.value
                  ? DemandPartnerTargetingMode.Include
                  : DemandPartnerTargetingMode.Exclude
              }
              onValueChange={(value) => {
                field.onChange(targetingModeIncluded[value] ?? false);
              }}
              aria-label={dashboardT('demandPartners.targetingLists.listMode')}
              className="mt-3 flex flex-wrap gap-4"
            >
              {Object.values(DemandPartnerTargetingMode).map((mode) => (
                <label key={mode} className="flex cursor-pointer items-center gap-1.5 text-xs">
                  <RadioGroupItem value={mode} />
                  {dashboardT(
                    mode === DemandPartnerTargetingMode.Include
                      ? 'demandPartners.targetingLists.include'
                      : 'demandPartners.targetingLists.exclude',
                  )}
                </label>
              ))}
            </RadioGroup>
          )}
        />
      </div>
      <FilePicker
        key={filePickerKey}
        className="min-w-0"
        label={dashboardT('demandPartners.targetingLists.csvFile')}
        buttonLabel={dashboardT('demandPartners.targetingLists.chooseFile')}
        buttonClassName="shrink-0"
        accept=".csv,text/csv"
        value={file}
        onFileChange={setFile}
        disabled={busy || !props.partnerId}
        helperText={dashboardT(targetingListHelperText(props.partnerId, bundle))}
        helperTextClassName="min-w-0 flex-1 truncate text-xs"
      />
      <div className="flex flex-wrap gap-2 xl:justify-end">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={busy}
          onClick={() => void downloadTemplate()}
        >
          <Download className="h-4 w-4" aria-hidden="true" />
          {dashboardT('demandPartners.targetingLists.template')}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={busy || !props.partnerId}
          onClick={() => void downloadCurrent()}
        >
          <Download className="h-4 w-4" aria-hidden="true" />
          {dashboardT('demandPartners.targetingLists.export')}
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={busy || !file || !props.partnerId}
          onClick={() => void upload()}
        >
          <Upload className="h-4 w-4" aria-hidden="true" />
          {dashboardT('demandPartners.targetingLists.upload')}
        </Button>
      </div>
    </div>
  );
}

function targetingListHelperText(partnerId: string, bundle: boolean) {
  if (!partnerId) return 'demandPartners.targetingLists.saveFirst' as const;
  if (bundle) return 'demandPartners.targetingLists.bundleColumns' as const;
  return 'demandPartners.targetingLists.domainColumns' as const;
}
